/**
 * WaiverWire — filter-driven waiver recommendations (Stage 2 rebuild)
 *
 * Replaces the old single streamed roster-wide analysis with a filter bar
 * (Position / Team / Stats / Schedule ease / Injury Insurance / JAI's Top 5)
 * that fires a single POST when the user hits Update — never on every
 * filter change, which is what keeps the Claude spend predictable (see
 * backend/src/services/waiverService.js's filter-signature cache).
 *
 * Landing state is the mascot empty state, not a loader — nothing has run
 * yet until the user asks for it. Once results come back, up to five ranked
 * player cards render: header (rank/name/position/team/ownership), team
 * role, key stats (shared StatTable), a code-computed risk chip, benefits
 * (shared JaiRichText), and up to three drop options with the preferred one
 * unmistakably dominant. The shared StickyAskBar sits pinned to the bottom
 * with the active filters + returned players as follow-up context.
 */

import { useState, useEffect, useMemo, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { leaguesApi } from '../services/api'
import { streamWaiver } from '../services/waiverStream'
import AppShell from '../components/AppShell'
import ToolHeader from '../components/ToolHeader'
import JaiRichText from '../components/JaiRichText'
import StatTable from '../components/StatTable'
import StickyAskBar from '../components/StickyAskBar'
import TeamChip from '../components/TeamChip'
import { JAI_FONTS } from '../components/JaiBrand'
import { V2, V2Semantic, V2List, V2ListRow, V2Icons, PrimaryBtn, PosChip, solidChip, pctTone, Notice } from '../components/JaiV2'
import { JAIMascot } from '../components/JAIMascot'
import { teamColor } from '../config/teamColors'
import { STAT_GLYPHS } from '../config/statGlyphs'

// ── Constants ────────────────────────────────────────────────────────────────

const EMPTY_FILTERS = {
  topFive: false, positions: [], teams: [], stats: [], scheduleEase: null, injuryInsurance: false,
}

// The filters endpoint returns display labels only (see waiverService.js's
// SCHEDULE_EASE_OPTIONS); these are the matching value keys the
// recommendations endpoint's `scheduleEase` field actually expects
// (waiverService.js's SCHEDULE_EASE_WINDOWS).
const SCHEDULE_EASE_KEYS = {
  'Easiest next 7 days': 'next_7_days',
  'Easiest next 3 weeks': 'next_3_weeks',
}

const RISK_CONFIG = {
  none:   { label: 'No Risk',     color: V2Semantic.hot },
  medium: { label: 'Medium Risk', color: V2.warning },
  high:   { label: 'High Risk',   color: V2Semantic.cold },
}

// ── Filter bar primitives ──────────────────────────────────────────────────

function ToggleChip({ label, checked, onChange, disabled }) {
  return (
    <button
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: checked ? V2.navy : 'white',
        border: `1px solid ${checked ? V2.navy : V2.hairSoft}`,
        borderRadius: 999, padding: '8px 14px', fontSize: 12.5, fontWeight: 700,
        color: checked ? 'white' : V2.navy, cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1, fontFamily: JAI_FONTS.tight, whiteSpace: 'nowrap',
        transition: 'background 150ms, opacity 150ms',
      }}
    >
      <span style={{
        width: 15, height: 15, borderRadius: 4, flexShrink: 0,
        border: `1.5px solid ${checked ? 'white' : 'rgba(10,31,68,0.25)'}`,
        background: checked ? 'white' : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {checked && <span style={{ color: V2.navy, display: 'flex', transform: 'scale(0.7)' }}>{V2Icons.check}</span>}
      </span>
      {label}
    </button>
  )
}

function OptionRow({ label, selected, onClick, swatch }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 12px', borderRadius: 9, border: 'none', textAlign: 'left',
        background: 'transparent', boxShadow: selected ? 'inset 3px 0 0 #1E5BD6' : 'none',
        cursor: 'pointer', fontFamily: JAI_FONTS.tight,
      }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = 'rgba(10,31,68,0.04)' }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = 'transparent' }}
    >
      {swatch && <span style={{ width: 9, height: 9, borderRadius: 3, background: swatch, flexShrink: 0 }} />}
      <span style={{
        flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: selected ? V2.blue : V2.navy,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {label}
      </span>
      {selected && <span style={{ color: V2.blue, display: 'flex', flexShrink: 0 }}>{V2Icons.check}</span>}
    </button>
  )
}

// One collapsible pill — Position/Team/Stats (multi) and Ease of Schedule
// (single) all use this shell, only the option list + selection logic differ.
function FilterPill({ pillKey, label, count, disabled, openKey, onOpen, minWidth = 220, children }) {
  const isOpen = openKey === pillKey
  return (
    <div data-filter-pill style={{ position: 'relative' }}>
      <button
        onClick={() => !disabled && onOpen(isOpen ? null : pillKey)}
        disabled={disabled}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'white',
          border: `1px solid ${isOpen ? V2.blue : V2.hairSoft}`,
          borderRadius: 999, padding: '8px 14px', fontSize: 12.5, fontWeight: 700,
          color: disabled ? V2.faint : V2.navy, cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.4 : 1, fontFamily: JAI_FONTS.tight,
          transition: 'background 150ms, opacity 150ms', whiteSpace: 'nowrap',
        }}
      >
        {label}
        {count > 0 && <span style={{ color: V2.blue }}>· {count}</span>}
        <span style={{ display: 'flex', color: V2.faint, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>
          {V2Icons.chevronDown}
        </span>
      </button>
      {isOpen && !disabled && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 30, minWidth,
          background: 'white', borderRadius: 14, border: `1px solid ${V2.hairSoft}`,
          boxShadow: '0 8px 32px rgba(10,31,68,0.14), 0 2px 8px rgba(10,31,68,0.06)',
          padding: 6, maxHeight: 320, overflowY: 'auto',
        }}>
          {children}
        </div>
      )}
    </div>
  )
}

// ── Filter bar ──────────────────────────────────────────────────────────────

function FilterBar({ filters, setFilters, filterOptions, sport, onUpdate, updating }) {
  const [openKey, setOpenKey] = useState(null)
  const barRef = useRef(null)

  // Close whichever pill is open on an outside click — every pill/panel is
  // marked with data-filter-pill so this only has to check one thing.
  useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target.closest('[data-filter-pill]')) setOpenKey(null)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const disabledByTopFive = filters.topFive

  const toggleInArray = (key, value) => {
    setFilters((prev) => {
      const current = prev[key]
      const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
      return { ...prev, [key]: next }
    })
  }

  return (
    <div ref={barRef} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
      <ToggleChip label="JAI's Top 5" checked={filters.topFive} onChange={(v) => setFilters((prev) => ({ ...prev, topFive: v }))} />

      <FilterPill pillKey="positions" label="Position" count={filters.positions.length} disabled={disabledByTopFive} openKey={openKey} onOpen={setOpenKey}>
        {(filterOptions?.positions || []).map((pos) => (
          <OptionRow key={pos} label={pos} selected={filters.positions.includes(pos)} onClick={() => toggleInArray('positions', pos)} />
        ))}
      </FilterPill>

      <FilterPill pillKey="teams" label="Team" count={filters.teams.length} disabled={disabledByTopFive} openKey={openKey} onOpen={setOpenKey} minWidth={240}>
        {(filterOptions?.teams || []).map((t) => (
          <OptionRow
            key={t.abbrev}
            label={t.name}
            selected={filters.teams.includes(t.abbrev)}
            onClick={() => toggleInArray('teams', t.abbrev)}
            swatch={teamColor(t.abbrev, sport).primary}
          />
        ))}
      </FilterPill>

      <FilterPill pillKey="schedule" label="Ease of Schedule" count={filters.scheduleEase ? 1 : 0} disabled={disabledByTopFive} openKey={openKey} onOpen={setOpenKey} minWidth={200}>
        {(filterOptions?.scheduleOptions || []).map((label) => {
          const value = SCHEDULE_EASE_KEYS[label]
          return (
            <OptionRow
              key={label}
              label={label}
              selected={filters.scheduleEase === value}
              onClick={() => setFilters((prev) => ({ ...prev, scheduleEase: prev.scheduleEase === value ? null : value }))}
            />
          )
        })}
      </FilterPill>

      <FilterPill pillKey="stats" label="Specific Stats" count={filters.stats.length} disabled={disabledByTopFive} openKey={openKey} onOpen={setOpenKey}>
        {(filterOptions?.stats || []).map((s) => (
          <OptionRow key={s.key} label={s.label} selected={filters.stats.includes(s.key)} onClick={() => toggleInArray('stats', s.key)} />
        ))}
      </FilterPill>

      <ToggleChip
        label="Injury Insurance"
        checked={filters.injuryInsurance}
        onChange={(v) => setFilters((prev) => ({ ...prev, injuryInsurance: v }))}
        disabled={disabledByTopFive}
      />

      <PrimaryBtn onClick={onUpdate} disabled={updating} credits="waiverWire" style={{ marginLeft: 'auto' }}>
        {updating ? 'Updating…' : 'Update'}
      </PrimaryBtn>
    </div>
  )
}

// ── Player card ─────────────────────────────────────────────────────────────

function statTableFromKeyStats(keyStats = []) {
  if (!keyStats.length) return null
  const columns = []
  const seen = new Set()
  keyStats.forEach((s) => {
    if (!seen.has(s.label)) { seen.add(s.label); columns.push({ key: s.label, label: s.label }) }
  })
  const rowMap = new Map()
  keyStats.forEach((s) => {
    if (!rowMap.has(s.window)) rowMap.set(s.window, {})
    rowMap.get(s.window)[s.label] = s.value
  })
  const order = ['Season', 'Last 7 Games']
  const rows = [...rowMap.entries()]
    .sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]))
    .map(([label, values]) => ({ label, values }))
  return { columns, rows }
}

function AvailabilityLine({ player }) {
  const bits = []
  if (player.availability) {
    bits.push(
      <span key="avail">
        Played {player.availability.played} of {player.availability.teamGames} games (
        <span style={{ color: pctTone(player.availability.percent), fontWeight: 700 }}>{player.availability.percent}%</span>)
      </span>
    )
  }
  if (player.recentInjuryMentions?.count) {
    const n = player.recentInjuryMentions.count
    bits.push(<span key="inj">{n} injury mention{n === 1 ? '' : 's'} in the last {player.recentInjuryMentions.windowDays} days</span>)
  }
  if (!bits.length) return null
  return (
    <div style={{ fontSize: 12.5, color: V2.faint, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {bits.map((b, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {i > 0 && <span>·</span>}
          {b}
        </span>
      ))}
    </div>
  )
}

function RiskChip({ risk, tags = [] }) {
  const cfg = RISK_CONFIG[risk] || RISK_CONFIG.none
  const chip = solidChip(cfg.color)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{
        fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 999,
        background: chip.background, color: chip.color,
        textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0,
      }}>
        {cfg.label}
      </span>
      {/* The chip is computed in code from injury and availability data; the tags are the
          model's own words. A tag like "Injury Prone" beside "No Risk" contradicts the
          computed answer, so tags only show when the computed risk is above none. */}
      {risk && risk !== 'none' && tags.map((t) => (
        <span key={t} style={{ fontSize: 11.5, fontWeight: 600, color: V2.faint }}>{t}</span>
      ))}
    </div>
  )
}

function DropOptions({ options = [] }) {
  if (!options.length) return null
  const preferred = options.find((o) => o.preferred) || options[0]
  const rest = options.filter((o) => o !== preferred)
  return (
    <div>
      <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.09em', textTransform: 'uppercase', color: V2.faint, marginBottom: 6 }}>
        Who to drop
      </div>
      <div style={{ background: V2.navy, borderRadius: 10, padding: '11px 14px', marginBottom: rest.length ? 6 : 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'white' }}>{preferred.name}</div>
        {preferred.reason && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.72)', marginTop: 2, lineHeight: 1.4 }}>{preferred.reason}</div>}
      </div>
      {rest.map((o, i) => (
        <div key={o.name} style={{ padding: '9px 14px', borderRadius: 10, border: `1px solid ${V2.hairSoft}`, marginBottom: i < rest.length - 1 ? 6 : 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: V2.navy }}>{o.name}</div>
          {o.reason && <div style={{ fontSize: 11.5, color: V2.faint, marginTop: 2, lineHeight: 1.4 }}>{o.reason}</div>}
        </div>
      ))}
    </div>
  )
}

function PlayerCard({ rank, player, sport, isTopPick }) {
  const stats = statTableFromKeyStats(player.keyStats)

  return (
    <V2ListRow style={{ flexDirection: 'column', alignItems: 'stretch', gap: 12, padding: '20px', position: 'relative' }}>
      {isTopPick && (
        <span style={{
          position: 'absolute', top: -9, left: 20, background: V2.blue, color: 'white',
          fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
          borderRadius: 6, padding: '3px 10px',
        }}>
          {STAT_GLYPHS.topPick} Top pick
        </span>
      )}

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: V2.faint, fontFamily: JAI_FONTS.mono, width: 22, flexShrink: 0 }}>
          {String(rank).padStart(2, '0')}
        </span>
        <span style={{ fontSize: 18, fontWeight: 700, color: V2.navy, flex: 1, minWidth: 120 }}>{player.name}</span>
        <PosChip>{player.position}</PosChip>
        <TeamChip abbr={player.team} sport={sport} />
        {player.ownershipPercent != null && (
          <span style={{ fontSize: 12.5, fontWeight: 700, color: pctTone(player.ownershipPercent), fontFamily: JAI_FONTS.mono, flexShrink: 0, marginLeft: 'auto' }}>
            {player.ownershipPercent}% owned
          </span>
        )}
      </div>

      {/* Team role */}
      {player.teamRole && <p style={{ fontSize: 15, color: V2.navy, lineHeight: 1.5, margin: 0 }}>{player.teamRole}</p>}

      {/* Key stats */}
      {stats && <StatTable title="Key stats" columns={stats.columns} rows={stats.rows} />}

      {/* Risk */}
      <RiskChip risk={player.risk} tags={player.riskTags} />

      {/* Availability / injury mentions */}
      <AvailabilityLine player={player} />

      {/* Benefits */}
      {player.benefits?.length > 0 && (
        <div>
          <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.09em', textTransform: 'uppercase', color: V2.faint, marginBottom: 6 }}>
            Benefits
          </div>
          <JaiRichText content={player.benefits.map((b) => `- ${b}`).join('\n')} />
        </div>
      )}

      {/* Who to drop */}
      <DropOptions options={player.dropOptions} />
    </V2ListRow>
  )
}

// ── Main component ─────────────────────────────────────────────────────────

// ── Progress while the model works ─────────────────────────────────────────
// Three honest steps (the ones the server reports), a running clock, and the
// count of recommendations written so far. No fake percentage: the model's
// writing time varies, so a bar that pretends to know would stall at "99%".
function WaiverProgress({ step, written, elapsed, sport }) {
  const steps = [
    'Reading your roster and the waiver wire',
    'Scoring the wire against your roster',
    sport === 'nfl' ? 'Ranking pickups and writing the reasons' : 'Ranking pickups and writing the reasons',
  ]
  return (
    <div role="status" aria-live="polite" data-testid="waiver-progress" style={{ display: 'flex', gap: 18, alignItems: 'center', padding: '22px 0 18px' }}>
      <JAIMascot size={56} state="thinking" animate />
      <div style={{ flex: 1, minWidth: 0 }}>
        {steps.map((label, i) => {
          const n = i + 1
          const state = n < step ? 'done' : n === step ? 'active' : 'todo'
          return (
            <div key={label} style={{ display: 'flex', alignItems: 'baseline', gap: 10, fontSize: 13.5, lineHeight: 1.9, color: state === 'todo' ? V2.faint : V2.navy, fontWeight: state === 'active' ? 700 : 500 }}>
              <span style={{ fontFamily: JAI_FONTS.mono, fontSize: 12, width: 14, color: state === 'done' ? V2.success : state === 'active' ? V2.blue : V2.faint }}>{state === 'done' ? '✓' : n}</span>
              <span>{label}{state === 'active' && n === 3 && written > 0 ? `: ${written} written` : ''}{state === 'active' ? '…' : ''}</span>
            </div>
          )
        })}
        <div style={{ fontSize: 12, color: V2.faint, marginTop: 4, fontFamily: JAI_FONTS.mono }}>
          {elapsed}s{elapsed < 8 ? ' · usually 10 to 20 seconds' : elapsed < 30 ? ' · nearly there, the last recommendations are being written' : ' · taking longer than usual, hang on'}
        </div>
      </div>
    </div>
  )
}

export default function WaiverWire() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()

  const [league, setLeague] = useState(null)
  const [filterOptions, setFilterOptions] = useState(null)
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState(null)

  // Player Analyser's "drop this player" verdict links here as
  // /league/:id/waiver?drop=Name (Directional Change 10) — same shape as
  // Trade Analyser's ?give=Name. Not a FilterBar option the user picks;
  // just a hint threaded into the request so the returned cards prefer this
  // player as the drop option (see waiverService.js's validateRecommendations).
  const [dropCandidate] = useState(() => searchParams.get('drop'))

  // Injury Insurance's handcuff CTA links here as /league/:id/waiver?target=Name
  // — the ADD direction, opposite of ?drop= above. Same shape, threaded into
  // the request so the returned cards guarantee this exact player (see
  // waiverService.js's validateRecommendations `targetName` param).
  const [targetCandidate] = useState(() => searchParams.get('target'))

  const [results, setResults] = useState(null)
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState('')
  // What the request is doing right now, so the page never looks frozen.
  const [progress, setProgress] = useState({ step: 1, written: 0 })
  const [elapsed, setElapsed] = useState(0)

  const sport = (league?.sport || 'nba').toLowerCase()

  useEffect(() => {
    const init = async () => {
      try {
        const [leagueRes, filtersRes] = await Promise.allSettled([
          leaguesApi.get(id),
          leaguesApi.getWaiverFilters(id),
        ])
        if (leagueRes.status === 'fulfilled') setLeague(leagueRes.value.data.league)
        else console.error('Waiver Wire: league failed to load', leagueRes.reason)
        if (filtersRes.status === 'fulfilled') setFilterOptions(filtersRes.value.data)
        else console.error('Waiver Wire: filters failed to load', filtersRes.reason)
      } catch (err) { console.error('Waiver Wire init failed', err) }
    }
    init()
    // A drop or target candidate means the user already knows what they
    // want — run the default search immediately rather than making them
    // hit Update.
    if (dropCandidate || targetCandidate) handleUpdate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // A seconds counter while a request runs: movement on screen is what tells
  // the user the page has not frozen.
  useEffect(() => {
    if (!updating) return undefined
    const started = Date.now()
    const timer = setInterval(() => setElapsed(Math.round((Date.now() - started) / 1000)), 1000)
    return () => clearInterval(timer)
  }, [updating])

  const handleUpdate = async () => {
    setUpdating(true)
    setError('')
    setProgress({ step: 1, written: 0 })
    setElapsed(0)
    setResults(null)
    const body = { ...filters }
    if (dropCandidate) body.dropCandidate = dropCandidate
    if (targetCandidate) body.target = targetCandidate
    try {
      let data
      try {
        // Cards appear as the model finishes them; the final answer replaces them.
        data = await streamWaiver(id, body, {
          onStatus: (e) => setProgress((p) => ({ ...p, step: e.step || p.step })),
          onPlayer: (player, written) => {
            setProgress((p) => ({ ...p, step: 3, written }))
            setResults((prev) => {
              const players = [...(prev?.players || [])]
              if (!players.some((x) => (x.playerId ?? x.name) === (player.playerId ?? player.name))) players.push(player)
              return { players, note: null, partial: true }
            })
          },
        })
      } catch (err) {
        if (!err.streamUnavailable) throw err
        // The stream could not start (old proxy, no streaming): the ordinary request.
        data = (await leaguesApi.getWaiverRecommendations(id, body)).data
      }
      setResults(data)
      setAppliedFilters(filters)
    } catch (err) {
      setResults(null)
      setError(err.response?.data?.error || err.message || 'Something went wrong. Try again.')
    } finally {
      setUpdating(false)
    }
  }

  // Context for the ask bar's follow-ups — the current filters + whatever
  // players came back, as plain text (no id for the backend to look this up
  // by, unlike Strategy/Review's DB rows — see StickyAskBar's own docblock).
  const askBarContext = useMemo(() => {
    if (!results?.players?.length) return undefined
    const f = appliedFilters || EMPTY_FILTERS
    const filterBits = [
      f.topFive && "JAI's Top 5",
      f.positions.length && `Position: ${f.positions.join(', ')}`,
      f.teams.length && `Team: ${f.teams.join(', ')}`,
      f.stats.length && `Stats: ${f.stats.join(', ')}`,
      f.scheduleEase && `Schedule: ${f.scheduleEase}`,
      f.injuryInsurance && 'Injury Insurance on',
    ].filter(Boolean).join(' | ') || 'None'

    const playersText = results.players.map((p) =>
      `- ${p.name} (${p.position || '?'}, ${p.team || '?'}) — ${p.teamRole || ''} — risk: ${p.risk}`
    ).join('\n')

    return `THE USER IS LOOKING AT THESE WAIVER WIRE RESULTS RIGHT NOW.\nActive filters: ${filterBits}\n${playersText}${results.note ? `\nNote: ${results.note}` : ''}`
  }, [results, appliedFilters])

  const hasRunOnce = results !== null

  return (
    <AppShell>
      <div style={{ padding: '30px 40px', fontFamily: JAI_FONTS.tight }}>
        <div style={{ maxWidth: 880, margin: '0 auto', paddingBottom: 100 }}>
          <ToolHeader
            leagueName={league?.name}
            title="Waiver Wire"
            subtitle={dropCandidate
              ? `Finding a real replacement for ${dropCandidate}`
              : targetCandidate
              ? (sport === 'nfl' ? `Adding ${targetCandidate} as your handcuff` : `Adding ${targetCandidate} as your Pick Up`)
              : "Set your filters and JAI finds the best real pickups on your league's wire"}
            back={{ label: 'Go to League Home Screen', to: `/league/${id}` }}
          />
          <FilterBar
            filters={filters}
            setFilters={setFilters}
            filterOptions={filterOptions}
            sport={sport}
            onUpdate={handleUpdate}
            updating={updating}
          />

          {/* Error banner */}
          {error && (
            <Notice tone="error" action="Try again" onAction={handleUpdate}>{error}</Notice>
          )}

          {/* Empty state — nothing run yet */}
          {!hasRunOnce && !updating && !error && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '64px 0' }}>
              <JAIMascot size={200} state="idle" />
              <p style={{ fontSize: 15, fontWeight: 700, color: V2.navy, marginTop: 20 }}>
                Select your filters to get JAI's waiver wire insights
              </p>
            </div>
          )}

          {/* While a request runs: what step it is on, how long it has been, and cards as they arrive. */}
          {updating && <WaiverProgress step={progress.step} written={progress.written} elapsed={elapsed} sport={sport} />}

          {/* Results (cards also show while the rest are still being written) */}
          {hasRunOnce && (
            <>
              {results.note && <JaiRichText content={results.note} style={{ marginBottom: 16 }} />}

              {results.players?.length > 0 ? (
                <V2List>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {results.players.map((p, i) => (
                      <PlayerCard key={p.playerId ?? p.name} rank={i + 1} player={p} sport={sport} isTopPick={i === 0} />
                    ))}
                  </div>
                </V2List>
              ) : (
                // Always shown when there are zero players — even alongside a
                // note. A note used to SUPPRESS this entirely, so a shortlist
                // that had real candidates but got a vague/generic note back
                // from Claude read as a broken page rather than an honest
                // "nothing matched." The note (when present) is now
                // supplementary context above this, never a replacement for it.
                <p style={{ fontSize: 15, color: V2.mute, textAlign: 'center', padding: '32px 0' }}>
                  No players on your wire match this filter combination.
                </p>
              )}
            </>
          )}
        </div>
      </div>

      <StickyAskBar
        leagueId={id}
        scope="waiver"
        context={askBarContext}
        placeholder={sport === 'nfl'
          ? 'Ask JAI a follow-up — e.g. "Should I drop my flex for this streamer?"'
          : 'Ask JAI a follow-up — e.g. "Should I drop Bane for DeRozan?"'}
      />
    </AppShell>
  )
}
